import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {Moment} from 'moment-timezone'
import {useCallback} from 'react'

import {materialDemandQueryOptions, MaterialDemandQueryParams, MaterialDemandQueryKey} from '../api'
import {getDemandDataPerMaterial} from '../selectors'
import {MaterialDemandByDay, DemandDataPerMaterial} from '../types'

export {MaterialDemandQueryKey}

/**
 * Time range represented as a tuple of Moment objects
 */
export type MomentRange = [Moment, Moment]

/**
 * Convert moment to date string in YYYY-MM-DD format
 */
const convertToDate = (moment: Moment): string => moment.format('YYYY-MM-DD')

export interface UseMaterialDemandPerMaterialQueryParams {
  plantCode: string
  timeFrame: MomentRange
  timezoneId: string
  selectedDate?: string
  options?: Omit<
    UseQueryOptions<MaterialDemandByDay, AxiosError, DemandDataPerMaterial>,
    'queryKey' | 'queryFn' | 'select'
  >
}

/**
 * Hook used for demand table - aggregates demand data for a range or single date
 * @param params - Query parameters including plant code, timeframe, timezone, and optional selected date
 * @returns Query result with demand data per material for table display
 */
export const useMaterialDemandPerMaterialQuery = ({
  plantCode,
  timeFrame,
  timezoneId,
  selectedDate,
  options
}: UseMaterialDemandPerMaterialQueryParams) => {
  const fromDate = convertToDate(timeFrame[0].clone().tz(timezoneId))
  const toDate = convertToDate(timeFrame[1].clone().tz(timezoneId))

  const queryParams: MaterialDemandQueryParams = {fromDate, toDate}

  const selector = useCallback(
    (data: MaterialDemandByDay) => {
      // handling the case when selectedDate is not in the data
      const dataFilteredByDate = selectedDate ? {[selectedDate]: data[selectedDate] ?? []} : data
      return getDemandDataPerMaterial(dataFilteredByDate)
    },
    [selectedDate]
  )

  return useQuery<MaterialDemandByDay, AxiosError, DemandDataPerMaterial>({
    ...materialDemandQueryOptions(plantCode, queryParams),
    select: selector,
    ...options
  })
}
